<template>
  <div
    :style="{ width: width, 'max-width': maxWidth }"
    :class="{ 'label-has-badge': labelHasBadge }"
  >
    <!-- Label -->
    <slot name="label" v-if="$slots.label" />
    <div class="q-mb-sm" v-if="label && !$slots.label">
      {{ label }}
      <span class="text-negative" v-if="required">*</span>
    </div>

    <!-- Description -->
    <slot name="description" v-if="$slots.description" />
    <div class="text-secondary q-mb-xs text-caption" v-if="description">
      {{ description }}
    </div>

    <q-select
      v-model="currentValue"
      :options="currentOptions"
      :option-value="optionValue"
      :option-label="optionLabel"
      :option-disable="optionDisable"
      :loading="loading"
      outlined
      dense
      options-dense
      :multiple="multiple"
      :clearable="clearable"
      :disable="disabled"
      :use-chips="useChips"
      emit-value
      map-options
      :use-input="useInput"
      lazy-rules
      :rules="[validateRequired]"
      hide-bottom-space
      bottom-slots
      fill-input
      :hide-selected="useInput && !multiple"
      @input="handleInput"
      @filter="filterFn"
      @filter-abort="abortFilterFn"
      @popup-show="$emit('popup-show')"
      @popup-hide="$emit('popup-hide')"
      dropdown-icon="keyboard_arrow_down"
      :bg-color="bgColor"
      style="width: 100%;"
    >
      <template v-slot:selected v-if="multiple && !useChips">
        {{ value.length + ' item(s) selected' }}
      </template>

      <template v-slot:no-option>
        <q-item>
          <q-item-section class="text-grey"> No results </q-item-section>
        </q-item>
      </template>

      <template v-slot:error> {{ errorMessage }} </template>

      <template v-slot:option="scope" v-if="labelHasBadge">
        <q-item v-bind="scope.itemProps" v-on="scope.itemEvents">
          <q-item-section>
            <q-item-label v-html="scope.opt.label" />
          </q-item-section>
          <q-item-section avatar v-if="scope.opt.badgeText">
            <Badge :label="scope.opt.badgeText" :color="scope.opt.badgeColor" align="top" />
          </q-item-section>
        </q-item>
      </template>

      <template v-slot:selected-item="scope" v-if="labelHasBadge">
        <q-item v-if="scope.opt.label" dense class="q-px-none">
          <q-item-section>
            <q-item-label v-html="scope.opt.label" />
          </q-item-section>
          <q-item-section avatar v-if="scope.opt.badgeText">
            <Badge :label="scope.opt.badgeText" :color="scope.opt.badgeColor" align="top" />
          </q-item-section>
        </q-item>
      </template>
    </q-select>
  </div>
</template>

<script>
import Badge from './Badge';
const messages = { en: {}, ja: {} };

export default {
  name: 'Selector',
  i18n: { messages },
  components: { Badge },
  props: {
    value: {
      type: [String, Number, Boolean, Array, Object],
    },
    options: {
      type: Array,
      required: true,
      default: () => {
        return [];
      },
    },
    optionValue: {
      type: String,
      default: 'value',
    },
    optionLabel: {
      type: String,
      default: 'label',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    optionDisable: {
      type: String,
      default: 'disabled',
    },
    labelHasBadge: {
      type: Boolean,
      default: false,
    },
    label: {
      type: [String, Number],
    },
    description: {
      type: String,
    },
    bgColor: {
      type: String,
      default: 'white',
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    useChips: {
      type: Boolean,
      default: false,
    },
    width: {
      type: String,
      default: '100%',
    },
    maxWidth: {
      type: String,
    },
    useInput: {
      type: Boolean,
      default: true,
    },
    required: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      currentOptions: this.options,
    };
  },
  computed: {
    currentValue: {
      set(val) {
        this.$emit('input', val);
      },
      get() {
        return this.value;
      },
    },
    isStringOptions() {
      return this.options.some((o) => typeof o === 'string');
    },
  },
  watch: {
    options(options) {
      this.currentOptions = options;
    },
  },
  mounted() {
    this.currentOptions = this.options;
  },
  methods: {
    handleInput(val) {
      this.$emit('input', val);
      this.$emit('change', val);
    },

    // Required validation.
    validateRequired(val) {
      return this.required && (!val || !val.length) ? this.$t('Please select.') : null;
    },

    filterFn(val, update) {
      // call abort() at any time if you can't retrieve data somehow
      update(
        () => {
          if (val === '') {
            this.currentOptions = this.options;
          } else {
            const needle = val.toLowerCase();
            const filterableValue = (v) => {
              return this.isStringOptions ? v : v[this.optionLabel];
            };
            this.currentOptions = this.options.filter((v) => {
              return filterableValue(v).toLowerCase().indexOf(needle) > -1;
            });
          }
        },

        // next function is available in Quasar v1.7.4+;
        // "ref" is the Vue reference to the QSelect
        (ref) => {
          if (val !== '' && ref.options.length > 0) {
            // DO NOT use setOptionIndex or moveOptionSelection with fill-input
            ref.setOptionIndex(-1); // reset optionIndex in case there is something selected
            ref.moveOptionSelection(1, true); // focus the first selectable option and do not update the input-value
          }
        }
      );
    },
    abortFilterFn() {
      // console.log('delayed filter aborted')
    },
  },
};
</script>

<style lang="scss" scoped>
.label-has-badge::v-deep .q-field__native {
  padding: 0;
}
</style>
